import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";

import { SimpleTestingControlListDto, StepIndex } from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { localizeAssignedAs } from "org/user/utils/localization";
import { WebRoute } from "routes";
import { canViewTestingProgram } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import { buildTagColumn, getUniqueTagIds } from "../sharedTableUtils";

import {
    TESTING_CONTROL_LIST_COLUMN_IDS,
    TESTING_CONTROL_TABLE_STATE_STORAGE_KEY,
    testingControlListColumnFiltersCodecMap,
    testingControlListTableDefaultValues,
} from "./consts";

export interface TestingControlRow {
    id: string;
    title: string;
    canView: boolean;
    testingProgramId: number;
    programName: string;
    programCanView: boolean;
    stepIndex: StepIndex;
    assignmentType: string;
    tagIds: string[];
    samplingTagIds: string[];
}

function buildRows(
    t: TFunction,
    data: SimpleTestingControlListDto,
    userId: number
): TestingControlRow[] {
    const resolveProgram = (testingProgramId: number) => {
        const programDto =
            data.linkedResources.testingProgramNamesDto[testingProgramId];
        return {
            programName: programDto?.resource ?? "",
            programCanView: programDto
                ? canViewTestingProgram(programDto.actions)
                : false,
        };
    };

    return data.content.flatMap(({ resource: control, actions }) => {
        const steps = [
            {
                assignedUserId: control.responsibleUserId,
                stepIndex: "Step0" as const,
            },
            ...(control.reviewerUserId
                ? [
                      {
                          assignedUserId: control.reviewerUserId,
                          stepIndex: "Step1" as const,
                      },
                  ]
                : []),
            ...(control.secondReviewerUserId
                ? [
                      {
                          assignedUserId: control.secondReviewerUserId,
                          stepIndex: "Step2" as const,
                      },
                  ]
                : []),
        ];

        const { programName, programCanView } = resolveProgram(
            control.testingProgramId
        );

        return steps
            .filter(({ assignedUserId }) => assignedUserId === userId)
            .map(({ stepIndex }) => ({
                id: control.id,
                title: control.title,
                canView: actions.view,
                testingProgramId: control.testingProgramId,
                programName,
                programCanView,
                stepIndex,
                assignmentType: localizeAssignedAs(t, stepIndex),
                tagIds: control.tags,
                samplingTagIds: control.samplingTags,
            }));
    });
}

export function useUserTestingControlListTable(
    data: SimpleTestingControlListDto,
    userId: number
) {
    const { t } = useTranslation();

    const tableData = useMemo(
        () => buildRows(t, data, userId),
        [t, data, userId]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        TESTING_CONTROL_TABLE_STATE_STORAGE_KEY,
        testingControlListTableDefaultValues,
        testingControlListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => `${row.id}-${row.stepIndex}`,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<TestingControlRow>(),
        getSortedRowModel: getSortedRowModel<TestingControlRow>(),
        getFilteredRowModel: getFilteredRowModel<TestingControlRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<TestingControlRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<TestingControlRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (
        column: Column<TestingControlRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, data);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<TestingControlRow>) =>
            column.id === TESTING_CONTROL_LIST_COLUMN_IDS.TAGS ||
            column.id === TESTING_CONTROL_LIST_COLUMN_IDS.SAMPLING_TAGS,
        tableId: TESTING_CONTROL_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<TestingControlRow>();
function buildColumns(t: TFunction, data: SimpleTestingControlListDto) {
    const { tags, tagCategories } = data.linkedResources;
    return [
        columnHelper.accessor((row) => row.title, {
            id: TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_CONTROL,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        to={`${WebRoute.TESTING_CONTROLS}/${row.id}`}
                        hasAccess={row.canView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingControlRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_CONTROL,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_TestingControls_TestingControl"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.programName, {
            id: TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_PROGRAM,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        to={`${WebRoute.TESTING_PROGRAMS}/${row.testingProgramId}`}
                        hasAccess={row.programCanView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingControlRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_PROGRAM,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_TestingControls_TestingProgram"),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.assignmentType, {
            id: TESTING_CONTROL_LIST_COLUMN_IDS.ASSIGNMENT_TYPE,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map((row) => row.original.stepIndex),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingControlRow, string[]>(
                (row, value) => value.includes(row.original.stepIndex)
            ),
            header: () =>
                t("AccessAndAssignment_TestingControls_AssignmentType"),
            size: TST_COLUMN_SIZE.L,
        }),
        buildTagColumn(columnHelper, {
            id: TESTING_CONTROL_LIST_COLUMN_IDS.TAGS,
            header: t("AccessAndAssignment_TestingControls_Tags"),
            selector: (row) => row.tagIds,
            tags,
            tagCategories,
        }),
        buildTagColumn(columnHelper, {
            id: TESTING_CONTROL_LIST_COLUMN_IDS.SAMPLING_TAGS,
            header: t("AccessAndAssignment_TestingControls_SamplingTags"),
            selector: (row) => row.samplingTagIds,
            tags,
            tagCategories,
        }),
    ];
}

type TestingControlListColumnFilters = ZodCodecMapToType<
    typeof testingControlListColumnFiltersCodecMap
>;

type TestingControlFilterableColumnId = keyof TestingControlListColumnFilters;

function getFilterComponent(
    t: TFunction,
    column: Column<TestingControlRow>,
    target: FilterTarget,
    data: SimpleTestingControlListDto
) {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingControlListColumnFilters>(column);
    const columnId = column.id as TestingControlFilterableColumnId;

    switch (columnId) {
        case TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_CONTROL:
        case TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_PROGRAM: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((option) => ({
                label: option,
                value: option,
                customLabel: <CustomEllipsisLabel text={option} />,
            }));
            const placeholderKey = {
                [TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_CONTROL]:
                    "AccessAndAssignment_TestingControls_TestingControl",
                [TESTING_CONTROL_LIST_COLUMN_IDS.TESTING_PROGRAM]:
                    "AccessAndAssignment_TestingControls_TestingProgram",
            } as const;
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(placeholderKey[columnId])}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case TESTING_CONTROL_LIST_COLUMN_IDS.ASSIGNMENT_TYPE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const stepIndexes = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.stepIndex)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...stepIndexes].map((stepIndex) => ({
                        label: localizeAssignedAs(t, stepIndex),
                        value: stepIndex,
                    }))}
                    placeholder={t(
                        "AccessAndAssignment_TestingControls_AssignmentType"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case TESTING_CONTROL_LIST_COLUMN_IDS.TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={data.linkedResources.tagCategories}
                    tags={data.linkedResources.tags}
                    uniqueTagIds={getUniqueTagIds(column, (row) => row.tagIds)}
                    placeholder={t("AccessAndAssignment_TestingControls_Tags")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case TESTING_CONTROL_LIST_COLUMN_IDS.SAMPLING_TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={data.linkedResources.tagCategories}
                    tags={data.linkedResources.tags}
                    uniqueTagIds={getUniqueTagIds(
                        column,
                        (row) => row.samplingTagIds
                    )}
                    placeholder={t(
                        "AccessAndAssignment_TestingControls_SamplingTags"
                    )}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
